// Subscription Types for Morafaa
import { LocalUsageStats } from '../services/localUsageService';
export interface SubscriptionPlan {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  price: number; // Monthly price in EGP
  currency: 'EGP';
  isFreeTier: boolean;
  
  // Usage Limits (simplified structure)
  maxOffices: number;
  maxClientsTotal: number; // Total clients across all offices
  maxFilesTotal: number;   // Total files across all clients
  
  // Feature Access
  includesEventReminders: boolean;
  includesAdvancedStats: boolean;
  includesCommunity: boolean;
  includesPhoneSupport: boolean;
  includesExportExcel: boolean;
  
  // Team Management
  teamMemberPrice?: number; // Price per team member for Professional plan
  maxTeamMembers?: number;
  
  // Billing
  billingCycle: 'monthly' | 'yearly';
  trialDays?: number;
  
  // UI Properties
  color: string;
  icon: string;
  popular?: boolean;
  features: string[];
  featuresAr: string[];
}

export interface Subscription {
  id: string;
  userId: string;
  userName: string;
  userPhone: string;
  planId: string;
  status: 'active' | 'expired' | 'cancelled' | 'trial' | 'past_due' | 'pending' | 'failed';
  
  // Dates
  startDate: string;
  endDate: string;
  
  
  // Extended trial metadata
  trialExtensionReason?: 'business_extension' | 'standard_trial' | 'admin_extension';
  
  // Billing
  totalPrice: number;
  currency: 'EGP';
  billingCycle?: 'monthly' | 'yearly';
  isActive?: boolean;
  
  // Metadata
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
}

export interface OfficeUsage {
  officeId: string;
  officeName: string;
  clientsCount: number;
  filesCount: number;
  lastUpdated: string;
}

export interface UsageStats {
  userId: string;
  officesCount: number;
  clientsCount: number;
  filesCount: number;
  lastUpdated: string;
  
  // Per-office breakdown
  officeUsage: OfficeUsage[];
}

export interface SubscriptionFeature {
  id: string;
  name: string;
  nameAr: string;
  description: string;
  descriptionAr: string;
  category: 'core' | 'premium' | 'team' | 'support';
  icon: string;
  requiredPlan: 'free' | 'basic' | 'professional';
}

export interface BillingInfo {
  id: string;
  userId: string;
  subscriptionId: string;
  
  // Payment Method
  paymentMethod: 'credit_card' | 'bank_transfer' | 'cash';
  cardLast4?: string;
  cardBrand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  
  // Billing Address
  billingName: string;
  billingAddress: string;
  billingCity: string;
  billingCountry: string;
  billingPostalCode: string;
  
  // Tax Information
  taxId?: string;
  vatNumber?: string;
  
  createdAt: string;
  updatedAt: string;
}

export interface PaymentHistory {
  id: string;
  userId: string;
  subscriptionId: string;
  
  // User Information
  userEmail: string;
  userName: string;
  userPhone: string;
  
  // Payment Details
  amount: number;
  transactionAmount: string;
  currency: 'EGP';
  transactionCurrency: 'EGP';
  status: 'pending' | 'completed' | 'failed' | 'refunded' | 'SUCCESS';
  paymentStatus: string;
  paymentMethod: string;
  
  // Card Information
  cardBrand: string;
  cardDataToken: string;
  maskedCard: string;
  
  // Transaction Details
  transactionId: string;
  orderId: string;
  orderReference: string;
  merchantOrderId: string;
  trxReferenceNumber: string;
  
  // Subscription Plan Details
  subscriptionPlanId: string;
  subscriptionPlanName: string;
  subscriptionPlanDescription: string;
  subscriptionPlanPrice: string;
  subscriptionPlanDuration: string;
  subscriptionPlanDurationType: 'monthly' | 'annually';
  
  // Security & Validation
  signature: string;
  mode: 'test' | 'live';
  
  // Language & Localization
  language: string;
  
  // Dates
  paymentDate: string;
  dueDate: string;
  
  // Invoice
  invoiceNumber: string;
  invoiceUrl?: string;
  
  // Metadata
  description: string;
  metadata?: any;
  
  // Audit Fields
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface SubscriptionLimits {
  maxOffices: number;
  maxClientsTotal: number; // Total clients across all offices
  maxFilesTotal: number;   // Total files across all clients
  maxTeamMembers?: number;
}

export interface FeatureAccess {
  eventReminders: boolean;
  advancedStats: boolean;
  community: boolean;
  phoneSupport: boolean;
  teamManagement: boolean;
  exportExcel: boolean;
}

export interface SubscriptionUpgrade {
  fromPlanId: string;
  toPlanId: string;
  prorationAmount: number;
  effectiveDate: string;
  reason: 'user_request' | 'automatic' | 'admin';
}

export interface SubscriptionDowngrade {
  fromPlanId: string;
  toPlanId: string;
  effectiveDate: string;
  reason: 'user_request' | 'payment_failed' | 'admin';
  dataRetention: boolean;
}

// API Response Types
export interface SubscriptionApiResponse {
  success: boolean;
  data?: Subscription;
  error?: string;
  message?: string;
}

export interface UsageStatsApiResponse {
  success: boolean;
  data?: LocalUsageStats;
  error?: string;
  message?: string;
}

export interface PlanValidationResult {
  isValid: boolean;
  canUpgrade: boolean;
  canDowngrade: boolean;
  limits: SubscriptionLimits;
  currentUsage: LocalUsageStats;
  exceededLimits: string[];
  suggestedPlan?: string;
}

// Subscription Plan IDs
export const SUBSCRIPTION_PLAN_IDS = {
  FREE: 'free',
  BASIC: 'basic',
  PROFESSIONAL: 'professional'
} as const;

export type SubscriptionPlanId = typeof SUBSCRIPTION_PLAN_IDS[keyof typeof SUBSCRIPTION_PLAN_IDS];

// Subscription Status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  EXPIRED: 'expired',
  CANCELLED: 'cancelled',
  TRIAL: 'trial',
  PAST_DUE: 'past_due'
} as const;

export type SubscriptionStatus = typeof SUBSCRIPTION_STATUS[keyof typeof SUBSCRIPTION_STATUS];
